<template>
  <div
    class="product-teaser"
    :data-qa-id="product.elevated ? 'sponsored' : undefined"
    :class="[
      {
        'product-teaser--recommendation': recommendation,
        'product-teaser--sponsored': product.sponsored,
        'product-teaser--transparent': transparent,
        'product-teaser--bestseller': bestseller,
        'product-teaser--no-pdp': isPdpLinkDisabled,
      },
    ]"
    :style="{ 'min-height': minHeight + 'px' }"
    @click.left.prevent="onClickLeft"
    @click.middle.prevent="onClickMiddle"
  >
    <div
      v-if="!bestseller && !recommendation"
      class="pt-sponsored"
      v-html="product.elevated ? $t('product.sponsored') : '&nbsp;'"
    />
    <div
      class="pt-figure"
      :class="[imageBackgroundColor ? 'pt-figure--background-color-' + imageBackgroundColor : '']"
    >
      <fn-image
        v-if="product.images?.length"
        :src="product.images[0].url"
        :alt="product.images[0].altText || product.images[0].description"
        :width="350"
        :height="350"
        :title="product.name"
        :transformation="'prod_s'"
      />
      <img
        v-else
        src="@/assets/img/product-placeholder.jpg"
        width="289"
        height="290"
        :alt="product.name"
      />
      <client-only>
        <fn-wishlist-button
          pt-old-style
          tertiary
          :data-name="recoName"
          :data-audience="recoAudience"
          :product-code="product.code"
          :bestseller="bestseller"
        />
      </client-only>
    </div>
    <div class="pt-content">
      <div class="pt-badges">
        <fn-product-badges
          v-if="product.badges?.length"
          :badges="productBadges"
          :discount="product.pricing?.savingsRelative?.formattedValue"
        />
      </div>

      <div class="pt-variant-info">
        <div
          v-for="(variantInfo, vIndex) in variantInformation"
          :key="vIndex"
          class="variant"
          :class="['variant--' + variantInfo.type]"
        >
          <span v-if="variantInfo.type !== 'color' || !product.colorCodes">
            {{ variantInfo.count }} {{ variantInfo.name }}
          </span>
          <div
            v-if="variantInfo.type === 'color' && product.colorCodes"
            class="color-swatches"
          >
            <span
              v-for="(color, index) in product.colorCodes"
              :key="index"
              :style="colorStyles(color)"
              class="color-swatch"
            />
          </div>
        </div>
        <p
          v-if="!hasStock && stockInfoText"
          class="pt-variant-info-stock"
        >
          {{ stockInfoText }}
        </p>
      </div>

      <component
        :is="tag"
        :href="link"
        :data-name="recoName"
        :data-audience="recoAudience"
        class="pt-header"
        @click.prevent
      >
        <div class="pt-subhead">
          {{ bestseller && freeText ? freeText : product.brandName }}
        </div>
        <div class="pt-head">
          {{ product.name }}
        </div>
      </component>

      <fn-rating
        v-if="showRating"
        :stars="recommendation || bestseller ? productAverageRating : productTotalAverageRating"
        :count="recommendation || bestseller ? productAverageRatingCount : productTotalAverageRatingCount"
      />

      <fn-pricing
        v-if="product.pricing"
        product-teaser
        :badges="product.badges"
        :price="product.pricing.current?.formattedValue"
        :former="product.pricing.former?.formattedValue"
        :former-price-savings="product.pricing.savings"
        :recommended-price="product.pricing.recommendedRetailPrice?.formattedValue"
        :per-unit="product.pricing.perUnit?.formattedValue"
        :friends-price="product.pricing.friendsPrice?.formattedValue"
        :friends-price-per-unit="product.pricing.friendsPricePerUnit?.formattedValue"
        :deal-badge-position-top="dealBadgePositionTop"
        :hidden-badges="['deal']"
      />

      <fn-buy-again-info
        v-if="buyAgain && timesBought && periodInMonths"
        :times-bought="timesBought"
        :period-in-months="periodInMonths"
      />
    </div>
    <div class="pt-actions">
      <fn-tooltip
        v-if="hasStock"
        class="pt-action pt-action--add-to-cart"
        placement="top-end"
        :open="addToCartButtonTooltipOpen"
      >
        <template #default>
          <fn-button
            id="addToCart"
            icon="add-to-cart-new-bold"
            :status="addToCartButtonStatus"
            :data-name="recoName"
            :data-audience="recoAudience"
            :aria-label="$t('accessibility.general.addToCart')"
            @click="handleAddToCart({ productCode: product.code, quantity: 1 })"
          />
        </template>
        <template #popover>
          <div class="pt-actions-tooltip">
            <template v-if="status.addToCart === 'lowStock'">
              {{ $t('cart.errors.lowStock') }}
            </template>
            <template v-else-if="status.addToCart === 'maxOrderQuantityExceeded'">
              {{ $t('cart.errors.maxOrderQuantityExceeded') }}
            </template>
            <template v-else-if="status.addToCart === 'itemLimitExceeded'">
              {{ $t('cart.errors.itemLimitExceeded') }}
            </template>
          </div>
        </template>
      </fn-tooltip>
      <fn-tooltip
        v-else
        class="pt-action pt-action--add-to-cart"
        :open="stockInfoTooltipOpen"
        placement="top-end"
      >
        <template #default>
          <fn-button
            id="stockInfo"
            class="pt-action pt-action--stock-info"
            icon="cart-new"
            :data-name="recoName"
            :data-audience="recoAudience"
            @click="handleStockInfo"
          />
        </template>
        <template #popover>
          <div
            v-if="stockInfoText || replenishmentDate"
            class="pt-actions-tooltip"
          >
            <p>
              {{ stockInfoText }}
              <span v-if="product.stock && replenishmentDate">
                {{ $t('shipping.availabilityDate', { availabilityDate: replenishmentDate }) }}
              </span>
            </p>
          </div>
        </template>
      </fn-tooltip>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState, mapWritableState } from 'pinia';
import { inStock } from '@/utils/stock';
import { getColorStyleCss } from '@/utils/colorStyles';
import { formatDateStringToDDMMYYYY } from '@/utils/date';
import { SHIPPING_DURATION_REPLENISHMENT_EARLY, SHIPPING_DURATION_REPLENISHMENT_MEDIUM } from '@/utils/shipping';
import ModLink from '@/mixins/ModLink';
import ProductBadges from '@/mixins/ProductBadges';

export default {
  mixins: [ModLink, ProductBadges],
  props: {
    product: {
      type: Object,
      required: true,
    },
    recommendation: {
      type: Boolean,
    },
    transparent: {
      type: Boolean,
    },
    imageBackgroundColor: {
      type: String,
      default: 'white',
    },
    bestseller: {
      type: Boolean,
    },
    recoName: {
      type: String,
      default: '',
    },
    recoAudience: {
      type: String,
      default: '',
    },
    freeText: {
      type: String,
      default: '',
    },
    buyAgain: {
      type: Boolean,
    },
    timesBought: {
      type: Number,
      default: null,
    },
    periodInMonths: {
      type: Number,
      default: null,
    },
    minHeight: {
      type: Number,
      default: 0,
    },
    showVariants: {
      type: Boolean,
      default: true,
    },
    showRating: {
      type: Boolean,
      default: true,
    },
    isSearchPage: {
      type: [Number, Boolean],
      default: false,
    },
    isPdpLinkDisabled: {
      type: Boolean,
      default: false,
    },
    buttonStatus: {
      type: String,
      required: false,
      default: null,
    },
    customAddToCartEvent: {
      type: Boolean,
    },
    dealBadgePositionTop: {
      type: Boolean,
    },
  },
  emits: ['addToCartEvent'],
  data: () => ({
    status: {
      addToCart: null,
    },
    stockInfoTooltipOpen: false,
  }),

  computed: {
    ...mapState(useMainStore, {
      ecomEnabledApp: 'getEcomEnabledApp',
      isAppview: 'isAppview',
      pdpBreadcrumbs: 'pdpBreadcrumbs',
    }),

    ...mapWritableState(useMainStore, {
      pdpSearchQuery: 'pdpSearchQuery',
    }),

    productAverageRating() {
      return this.product.averageRating ? this.product.averageRating : 0;
    },

    productAverageRatingCount() {
      return this.product.numberOfReviews ? this.product.numberOfReviews : null;
    },

    productTotalAverageRating() {
      return this.product.totalAvgRating ? this.product.totalAvgRating : this.productAverageRating;
    },

    productTotalAverageRatingCount() {
      return this.product.totalNumberOfReviews ? this.product.totalNumberOfReviews : this.productAverageRatingCount;
    },

    replenishmentDate() {
      if (this.product.stock.replenishmentDate) {
        return formatDateStringToDDMMYYYY(this.product.stock.replenishmentDate);
      }

      return '';
    },

    variantInformation() {
      const variants = [];

      if (this.product.variantAttributeMetas) {
        for (let i = 0; i < this.product.variantAttributeMetas.length; i += 1) {
          const variantAttributeMetas = this.product.variantAttributeMetas[i];
          variants.push({
            type: variantAttributeMetas?.code?.toLowerCase(),
            count: variantAttributeMetas?.count,
            name: variantAttributeMetas?.count === 1 ? variantAttributeMetas.name : variantAttributeMetas.plural,
          });
        }
      }
      return variants;
    },

    link() {
      return this.isPdpLinkDisabled ? '' : this.getModLink(this.product.url) || this.product.url;
    },

    tag() {
      return this.link ? 'a' : 'div';
    },

    addToCartButtonStatus() {
      if (this.buttonStatus && ['error', 'loading', 'success'].includes(this.buttonStatus)) {
        return this.buttonStatus;
      }

      if (this.status.addToCart === 'error') {
        return 'error';
      }

      if (this.status.addToCart === 'loading') {
        return 'loading';
      }

      if (this.status.addToCart === null) {
        return null;
      }

      return 'success';
    },

    addToCartButtonTooltipOpen() {
      return (
        this.status.addToCart === 'lowStock' ||
        this.status.addToCart === 'maxOrderQuantityExceeded' ||
        this.status.addToCart === 'itemLimitExceeded'
      );
    },

    hasStock() {
      return (
        inStock(this.product?.stock?.stockLevelStatus) ||
        (!this.product.stock && (this.recommendation || !!this.recoName))
      );
    },

    stockInfoText() {
      const isEarlyShippingDuration = this.product?.stock?.stockLevelStatus === SHIPPING_DURATION_REPLENISHMENT_EARLY;
      const isMediumShippingDuration = this.product?.stock?.stockLevelStatus === SHIPPING_DURATION_REPLENISHMENT_MEDIUM;

      if (isEarlyShippingDuration) {
        return this.$t('shipping.status.shortlyAvailable');
      }

      if (isMediumShippingDuration) {
        return this.$t('shipping.status.currentlyNotAvailable');
      }

      return null;
    },
  },

  methods: {
    ...mapActions(useCartStore, {
      addEntry: 'addEntry',
    }),

    colorStyles(color) {
      return getColorStyleCss(color);
    },

    handleAddToCart({ productCode, quantity }) {
      if (this.customAddToCartEvent) {
        return this.$emit('addToCartEvent', { productCode, quantity });
      }

      if (this.isAppview && !this.ecomEnabledApp) {
        return window.open(this.link, '_blank');
      }

      if (this.status.addToCart === 'success' && !this.isAppview) {
        return this.$router.push({ path: this.$t('url.cart') });
      }

      this.status.addToCart = 'loading';

      this.addEntry({ cartFields: 'FULL', productCode, quantity })
        .then((cartModification) => {
          this.status.addToCart = cartModification.statusCode;
        })
        .catch(() => {
          this.status.addToCart = 'error';
        })
        .finally(() => {
          setTimeout(() => {
            this.status.addToCart = null;
          }, 5000);
        });

      return undefined;
    },

    handleStockInfo() {
      this.stockInfoTooltipOpen = !this.stockInfoTooltipOpen;
    },

    async onClickLeft() {
      if (this.recommendation) {
        this.pdpBreadcrumbs.nodes = [];
      }

      if (this.isPdpLinkDisabled) {
        return;
      }

      if (this.isSearchPage) {
        this.pdpSearchQuery = this.$route.query;
      }

      return navigateTo(this.link);
    },

    onClickMiddle() {
      window.open(this.link, '_blank');
    },
  },
};
</script>

<style lang="scss" scoped>
.product-teaser {
  position: relative;
  height: 100%;
  display: flex;
  background-color: $color-general-background-neutral-default;
  border-bottom: 10px solid transparent;
  cursor: pointer;
  transition: box-shadow 0.3s ease-in-out;
  text-decoration: none;
  color: palette(black);
  border-radius: map-get($border-radiuses, 'l');
  flex-direction: column;
  flex-wrap: nowrap;
  box-sizing: border-box;

  &:hover {
    .pt-figure {
      padding: 0;
    }
  }

  .pt-sponsored {
    flex: 0 1 100%;
    padding: math.div($pad * 0.625, 2) $pad * 0.625;
    font-size: map-get($font-sizes, 'm');
    color: $color-general-text-neutral-low;
    position: absolute;
    top: -31px;
  }

  .pt-figure {
    display: flex;
    flex-direction: column;
    position: relative;
    transition-property: border-radius, border-color;
    transition-duration: 0.3s;
    transition-timing-function: ease-in-out;
    border-radius: map-get($border-radiuses, 'l');
    order: 1;
    flex: 0 0 auto;
    margin-bottom: $pad;
    padding: math.div($pad, 2);
    transition: padding 0.3s;

    img {
      display: block;
      width: 100%;
    }
  }

  .pt-content {
    padding: 0 math.div($pad, 2) math.div($pad, 2) math.div($pad, 2);
    min-width: 0; // pt-head text-overflow fix
    max-width: 100%;
    order: 3;
    flex: 0 1 auto;
  }

  .pt-badges {
    margin-bottom: math.div($pad, 2);
  }

  .pt-actions {
    order: 2;
    position: relative;

    .pt-action--add-to-cart {
      position: absolute;
      bottom: 100%;
      right: math.div($pad, 2);

      &:deep(.button--icon-only.button--status-loading) {
        min-height: $ui-height;
        min-width: $ui-height;
      }
    }

    .pt-action--stock-info {
      background-color: mix($color-general-background-neutral-default, palette(black), 85%);
    }
  }

  .pt-header {
    text-decoration: none;
  }

  .pt-head {
    color: $color-general-text-neutral-default;
    font-size: map-get($label, 'large');
    font-weight: map-get($font-weights, 'medium-bold');
    line-height: 1.2em;
    display: -webkit-box;
    margin: 0 0 math.div($pad, 4) 0;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
  }

  .pt-subhead {
    font-size: map-get($overline, 'medium');
    font-weight: map-get($font-weights, 'medium');
    color: $color-general-text-neutral-default;
    line-height: 1.3em;
  }

  .pt-variant-info {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;

    .variant {
      color: $color-general-text-neutral-lower;
      font-size: map-get($font-sizes, 's');
      margin-bottom: math.div($pad, 4);

      .color-swatches {
        display: flex;
        flex-wrap: wrap;

        .color-swatch {
          display: inline-block;
          width: $pad;
          height: $pad;
          margin: 0 math.div($pad, 2) math.div($pad, 2) 0;
          border-radius: 50%;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
        }
      }

      &:has(+ .variant) {
        margin: math.div($pad, 4) 0 0 0;
      }
    }
  }

  .pt-variant-info-stock {
    color: $color-general-text-neutral-lower;
    margin-bottom: 0;
    font-size: map-get($font-sizes, 's');
    line-height: map-get($font-sizes, 's');
  }

  &.product-teaser--sponsored {
    margin-top: 31px;
  }

  &.product-teaser--transparent {
    background: transparent;
  }

  &.product-teaser--recommendation {
    @include subgrid-inherit();
    width: 100%;
    margin: 0;

    :deep(.pt-content),
    :deep(.pricing) {
      @include subgrid-inherit();
    }
  }

  &.product-teaser--no-pdp {
    cursor: default;
  }

  @each $palette, $tones in $palettes {
    @each $tone, $hex in $tones {
      @if $tone == 'base' {
        &,
        &.product-teaser--recommendation {
          .pt-figure--background-color-#{$palette} {
            background-color: $hex;
            border-color: $hex;
          }
        }
      } @else {
        &,
        &.product-teaser--recommendation {
          .pt-figure--background-color-#{$palette}-#{$tone} {
            background-color: $hex;
            border-color: $hex;
          }
        }
      }
    }
  }
}

.pt-actions-tooltip {
  padding: $pad;
  max-width: 310px;

  @include bp(m) {
    max-width: 160px;
  }

  @include bp(l) {
    max-width: 200px;
  }

  span {
    display: block;
  }
}
</style>
